import { FormTypes } from '@oneblink/types'
import { FormElement } from '@oneblink/types/typescript/forms'

interface HasChildren {
  elements?: FormElement[]
}

export function insertAfter(
  elements: FormTypes.FormElement[],
  targetElement: FormTypes.FormElement,
  insertElement: FormTypes.FormElement,
): FormTypes.FormElement[] {
  return elements.reduce((newElements, element) => {
    newElements.push(element)
    if (element === targetElement) {
      newElements.push(insertElement)
    }
    return newElements
  }, [] as FormTypes.FormElement[])
}

export function replaceElement(
  elements: FormTypes.FormElement[],
  targetElement: FormTypes.FormElement,
  insertElement: FormTypes.FormElement,
): FormTypes.FormElement[] {
  return elements.reduce((newElements, element) => {
    if (element === targetElement) {
      newElements.push(insertElement)
    } else {
      newElements.push(element)
    }
    return newElements
  }, [] as FormTypes.FormElement[])
}

export function findElement<T extends FormTypes.FormElement>(
  parentElement: HasChildren,
  elementNameToFind: string,
): {
  parent: HasChildren
  value: T
} | void {
  for (const iterElement of parentElement.elements || []) {
    switch (iterElement.type) {
      case 'form':
      case 'repeatableSet':
      case 'page':
      case 'infoPage':
      case 'section':
        const result = findElement<T>(
          iterElement as HasChildren,
          elementNameToFind,
        )
        if (result) return result
        break
      default:
        if (iterElement.name === elementNameToFind) {
          return { parent: parentElement, value: iterElement as T }
        }
    }
  }
}

export function findPage(definition: FormTypes.Form, pageLabel: string) {
  const pageElement = definition.elements.find(
    (element) => element.type === 'page' && element.label === pageLabel,
  )

  if (pageElement) {
    return pageElement as FormTypes.PageElement
  }
}

export function findSubForm(
  parentElement: HasChildren,
  formNameToFind: string,
): {
  parent: HasChildren
  value: FormTypes.FormFormElement
} | void {
  for (const iterElement of parentElement.elements || []) {
    if (iterElement.type === 'form' && iterElement.name === formNameToFind) {
      return { parent: parentElement, value: iterElement }
    }
    switch (iterElement.type) {
      case 'form':
      case 'repeatableSet':
      case 'page':
      case 'infoPage':
      case 'section':
        const result = findSubForm(iterElement as HasChildren, formNameToFind)
        if (result) return result
        break
    }
  }
}

export function updateOptions({
  definition,
  elementName,
  options,
}: {
  definition: FormTypes.Form
  elementName: string
  options: FormTypes.ChoiceElementOption[]
}) {
  const element = findElement(definition, elementName)

  if (element?.value.type === 'select' && element.parent.elements) {
    element.parent.elements = replaceElement(
      element.parent.elements,
      element.value,
      {
        ...element.value,
        options,
      },
    )
    return true
  }
  return false
}

